import { useState, useCallback, useEffect } from 'react';
import { getOrdersHistory } from 'redux/actions/orders';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper } from '@mui/material';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import OrderView from 'components/Order/OrderView';
import OrdersHistoryList from 'components/Order/OrdersHistoryList';
import useQuery from 'hooks/useQuery';
import { ORDERS_FILTER_STATUS_HISTORY } from 'constants/config';
import { ORDERS_HISTORY_LOADING } from 'redux/actions/types';
import ErrorBoundaryWrapper from 'components/ErrorBoundaryWrapper';
import dayjs from 'dayjs';
import Preloader from 'components/Preloader';
import * as all from 'constants/permissions';
import usePermission from 'hooks/permission';
import { useFilteredProviders } from 'components/Order/hooks/useFilteredProviders';
dayjs.extend(utc);
dayjs.extend(timezone);
const OrdersHistory = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { search } = useLocation();
  localStorage.removeItem('redirectUrl');
  const { currentBusiness } = useSelector(({ business }) => business);
  const { data } = useSelector(({ user }) => user);
  const [order, setOrder] = useState(null);
  const [pageSize, setPageSize] = useState(5);
  const [pageTable, setPage] = useState(0);
  const { orderHistoryList, orderHistoryCount, orderHistoryLoading } =
    useSelector(({ orders }) => orders);
  const { locationIdsForHistory, brandIdsForHistory, filterBrandList } =
    useSelector(({ business }) => business);
  let { current } = useParams();
  const [orderLoading, setOrderLoading] = useState(false);
  const query = useQuery();
  const checkPermission = usePermission();
  const startDateQuery = query.get('start');
  const endDateQuery = query.get('end');
  const skipQuery = query.get('skip') || 0;
  const { filteredProviders } = useFilteredProviders();
  const statusesList =
    Object.values(ORDERS_FILTER_STATUS_HISTORY).map((item) => item.value) || [];
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (currentBusiness?._id) {
      const idsBrandOrderHistory = [];
      filterBrandList.forEach((item) => {
        if (item?._id) {
          return idsBrandOrderHistory.push(item?._id);
        }
      });
      const selectedBrand = JSON.parse(localStorage.getItem('selectedBrand'));
      const selectedLocation = JSON.parse(
        localStorage.getItem('selectedLocation')
      );
      const providers = Object.keys(filteredProviders);
      const body = {
        start: startDateQuery
          ? Number(startDateQuery)
          : dayjs()
              .tz(currentBusiness.timezone?.timeZoneId)
              .subtract(1, 'w')
              .startOf('day')
              .valueOf(),
        end: endDateQuery
          ? Number(endDateQuery)
          : dayjs()
              .tz(currentBusiness.timezone?.timeZoneId)
              .endOf('day')
              .valueOf(),
        skip: Number(skipQuery),
        limit: Number(pageSize),
        providers: providers,
        statuses: statusesList,
        brandIds: checkPermission(all['SUPER'])
          ? [selectedBrand?.id]
          : brandIdsForHistory.length === 0
          ? idsBrandOrderHistory
          : brandIdsForHistory || [],
        locationIds: checkPermission(all['SUPER'])
          ? [selectedLocation?.value?._id]
          : locationIdsForHistory || [],
      };
      localStorage.setItem('requestData', JSON.stringify(body));
      const skip = pageTable * pageSize;
      history.push(
        `${history.location.pathname}?skip=${skip}&limit=${pageSize}&page=${pageTable}`
      );
      dispatch({ type: ORDERS_HISTORY_LOADING, payload: true });
      setOrderLoading(true);
      dispatch(getOrdersHistory(body, pageTable)).then(() => {
        setOrderLoading(false);
        dispatch({ type: ORDERS_HISTORY_LOADING, payload: false });
      });
    }
  }, [currentBusiness?._id]);

  const findOrderById = (orders, id) => {
    for (let order of orders) {
      if (order?._id === id) {
        return order;
      }
      if (order?.suborders) {
        const foundSubOrder = order?.suborders?.find(
          (subOrder) => subOrder?._id === id
        );
        if (foundSubOrder) {
          return { ...foundSubOrder, business: order?.business };
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (current) {
      const orderData = findOrderById(orderHistoryList, current);
      if (
        orderData &&
        order &&
        order?.status?.status !== orderData?.status?.status &&
        current === order?._id
      ) {
        orderData.status.status = order?.status?.status;
      }
      if (orderData) {
        setOrder(orderData);
      }
    } else {
      setOrder(null);
    }
  }, [current, history, orderHistoryList]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_ORDER_STATUS',
      payload: { orderId: order?._id, newStatus: order?.status?.status },
    });
  }, [order?.status?.status]);

  const handleSelect = useCallback(
    (row) => {
      const { _id } = row;

      history.replace({
        pathname: `/history${current === _id ? '' : `/${_id}`}`,
        search,
      });
    },
    [current, history, search]
  );

  const resizeHandler = () => {
    if (window.innerWidth < 1000) {
      setShow(false);
    } else {
      setShow(true);
    }
  };

  useEffect(() => {
    resizeHandler();
  }, [show]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [show]);

  return (
    <ErrorBoundaryWrapper>
      <Box display="flex">
        <Paper className="order-content">
          <Box className="orderListBlock">
            <OrdersHistoryList
              data={orderHistoryList}
              count={orderHistoryCount}
              setPageSize={setPageSize}
              setPage={setPage}
              pageTable={pageTable}
              pageSize={pageSize}
              onSelect={handleSelect}
              loading={orderHistoryLoading}
            />
          </Box>
        </Paper>
        {orderHistoryLoading && <Preloader overlay />}
        {(show && !orderHistoryLoading) || (current && !orderHistoryLoading) ? (
          <OrderView
            orderList={orderHistoryList}
            page={pageTable}
            data={order}
            business={data}
            orderId={current}
            loading={orderLoading}
            handleSelect={handleSelect}
            setOrder={setOrder}
          />
        ) : (
          <></>
        )}
      </Box>
    </ErrorBoundaryWrapper>
  );
};

export default OrdersHistory;
